import React, { useState } from "react";
import { ChevronDown, ChevronUp, Shield, Eye, CreditCard, HardDrive, Lock, Cookie, Database, UserCheck, Mail, AlertTriangle } from "lucide-react";
import SEO from "../components/SEO";

const PrivacyPolicy = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (index) => {
    setExpandedSection(expandedSection === index ? null : index);
  };

  const sections = [
    {
      icon: Database,
      title: "1. Information We Collect",
      content: "We collect information you provide directly to us, such as when you create an account, make a purchase, upload content, or contact us. This may include your name, email address, phone number, uploaded files, and any other information you choose to provide.",
      importance: "high"
    },
    {
      icon: Eye,
      title: "2. How We Use Your Information",
      content: "We use the information we collect to provide, maintain, and improve our services, process your purchases and payments, deliver content, communicate with you, and ensure the security of our platform.",
      importance: "high"
    },
    {
      icon: CreditCard,
      title: "3. Payment Information and Razorpay",
      content: "We use Razorpay as our payment gateway to process all transactions. When you make a purchase, your payment information (card details, UPI, etc.) is collected and processed directly by Razorpay. We do not store your payment card details on our servers. Razorpay collects and processes your payment data in accordance with their privacy policy and PCI-DSS compliance standards. We only receive transaction confirmation and basic purchase details from Razorpay.",
      importance: "high"
    },
    {
      icon: HardDrive,
      title: "4. Content Delivery and Google Drive",
      content: "Study materials and educational content are stored on and delivered through Google Drive. When you purchase content, we share necessary information (such as your email address) with Google Drive to grant you access to the purchased materials. Google may collect information about your access to these files according to their privacy policy. We recommend reviewing Google's Privacy Policy to understand how they handle your data.",
      importance: "high"
    },
    {
      icon: Shield,
      title: "5. Information Sharing with Third Parties",
      content: "We share your information with third-party service providers only as necessary to deliver our services. This includes: (1) Razorpay for payment processing, (2) Google Drive for content storage and delivery, and (3) email service providers for communications. We do not sell, trade, or otherwise transfer your personal information to unrelated third parties without your consent, except as required by law.",
      importance: "high"
    },
    {
      icon: Lock,
      title: "6. Data Security",
      content: "We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. This includes encryption, secure servers, and regular security assessments. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.",
      importance: "high"
    },
    {
      icon: Cookie,
      title: "7. Cookies and Tracking",
      content: "We use cookies and similar technologies to enhance your experience, analyze usage patterns, maintain your session, and improve our services. You can control cookie settings through your browser preferences.",
      importance: "medium"
    },
    {
      icon: Database,
      title: "8. Data Retention",
      content: "We retain your personal information for as long as necessary to provide our services and comply with legal obligations. Purchase records and transaction history are retained for accounting and legal compliance purposes. You may request deletion of your data at any time, subject to legal retention requirements.",
      importance: "medium"
    },
    {
      icon: UserCheck,
      title: "9. Your Rights",
      content: "You have the right to access, update, or delete your personal information. You can also opt out of certain communications and request data portability. For payment-related data held by Razorpay, you may need to contact them directly. For content access through Google Drive, you can manage permissions in your Google account settings.",
      importance: "high"
    }
  ];

  const securityMeasures = [
    {
      title: "Encryption",
      description: "All data is encrypted in transit and at rest using industry-standard protocols",
      icon: "🔐"
    },
    {
      title: "Access Controls",
      description: "Strict access controls and authentication mechanisms protect your data",
      icon: "🛡️"
    },
    {
      title: "Regular Audits",
      description: "We conduct regular security audits and vulnerability assessments",
      icon: "🔍"
    },
    {
      title: "Secure Infrastructure",
      description: "Our infrastructure is built on secure cloud platforms with redundancy",
      icon: "☁️"
    }
  ];

  return (
    <>
      <SEO 
        title="Privacy Policy - ReferMe"
        description="Learn how ReferMe protects your personal information, handles data, and ensures your privacy when using our educational platform."
        url="/privacy"
      />
      <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 py-16 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-2xl mb-6 shadow-lg shadow-indigo-500/30">
            <Shield className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Privacy Policy
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-3">
            Your privacy is important to us. Learn how we collect, use, and protect your data.
          </p>
          <div className="inline-flex items-center gap-2 text-sm text-gray-500">
            <Lock className="w-4 h-4" />
            <span>Last updated: November 9, 2025</span>
          </div>
        </div>

        {/* Privacy Commitment Banner */}
        <div className="mb-10">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 p-6">
            <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-emerald-500/5 backdrop-blur-sm"></div>
            <div className="relative flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <Lock className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-green-300 mb-2">Our Privacy Commitment</h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  At ReferMe, we are committed to protecting your privacy and ensuring the security of your personal information. We believe in transparency and will always be clear about how we handle your data.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Table of Contents */}
        <div className="mb-10">
          <div className="bg-gradient-to-br from-gray-900/80 via-gray-900/60 to-gray-900/80 backdrop-blur-lg rounded-2xl p-6 border border-gray-800/50 shadow-xl">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Eye className="w-5 h-5 text-indigo-400" />
              Table of Contents
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {sections.map((section, index) => (
                <button
                  key={index}
                  onClick={() => {
                    const element = document.getElementById(`section-${index}`);
                    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="text-left px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-indigo-400 hover:bg-gray-800/50 transition-all duration-200"
                >
                  {section.title}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Privacy Policy Sections - Accordion Style */}
        <div className="mb-10">
          <h2 className="text-2xl font-bold text-white mb-6">Detailed Privacy Information</h2>
          <div className="space-y-3">
            {sections.map((section, index) => {
              const Icon = section.icon;
              const isExpanded = expandedSection === index;
              const importanceColors = {
                high: "border-red-500/30 bg-red-500/5",
                medium: "border-gray-700/50 bg-gray-800/30",
              };
              
              return (
                <div
                  key={index}
                  id={`section-${index}`}
                  className={`bg-gradient-to-br from-gray-900/80 via-gray-900/60 to-gray-900/80 backdrop-blur-lg rounded-xl border ${
                    importanceColors[section.importance]
                  } overflow-hidden transition-all duration-300 hover:border-indigo-500/30`}
                >
                  <button
                    onClick={() => toggleSection(index)}
                    className="w-full px-6 py-4 flex items-center justify-between gap-4 text-left hover:bg-gray-800/30 transition-all duration-200"
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div className={`flex-shrink-0 w-10 h-10 rounded-lg ${
                        section.importance === 'high' 
                          ? 'bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600' 
                          : 'bg-gray-800'
                      } flex items-center justify-center shadow-lg`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-white mb-1">
                          {section.title}
                        </h3>
                        {section.importance === 'high' && (
                          <span className="inline-flex items-center text-xs text-amber-400">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Important
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </button>
                  
                  {isExpanded && (
                    <div className="px-6 pb-6 pt-2">
                      <div className="pl-14">
                        <p className="text-gray-300 leading-relaxed text-[15px]">
                          {section.content}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Security Measures */}
        <div className="mb-10">
          <h2 className="text-2xl font-bold text-white mb-6">How We Protect Your Data</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {securityMeasures.map((measure, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-900/80 via-gray-900/60 to-gray-900/80 backdrop-blur-lg rounded-xl p-6 border border-gray-800/50 shadow-xl hover:border-indigo-500/30 transition-all duration-300 text-center"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 mb-4 shadow-lg">
                  <span className="text-2xl">{measure.icon}</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">
                  {measure.title}
                </h3>
                <p className="text-gray-400 text-xs leading-relaxed">
                  {measure.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Your Rights */}
        <div className="mb-10">
          <h2 className="text-2xl font-bold text-white mb-6">Your Privacy Rights</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gradient-to-br from-green-900/20 via-green-900/10 to-gray-900/20 backdrop-blur-lg rounded-xl p-6 border border-green-500/20 shadow-xl">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <UserCheck className="w-5 h-5 text-green-400" />
                </div>
                <h3 className="text-xl font-bold text-green-400">
                  Your Rights
                </h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-gray-300">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-500/20 border border-green-500/40 flex items-center justify-center mt-0.5">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  </div>
                  <span className="text-sm leading-relaxed">Access your personal data</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-500/20 border border-green-500/40 flex items-center justify-center mt-0.5">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  </div>
                  <span className="text-sm leading-relaxed">Update or correct your information</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-500/20 border border-green-500/40 flex items-center justify-center mt-0.5">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  </div>
                  <span className="text-sm leading-relaxed">Request deletion of your data</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-500/20 border border-green-500/40 flex items-center justify-center mt-0.5">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  </div>
                  <span className="text-sm leading-relaxed">Opt out of communications</span>
                </li>
                <li className="flex items-start gap-3 text-gray-300">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-500/20 border border-green-500/40 flex items-center justify-center mt-0.5">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  </div>
                  <span className="text-sm leading-relaxed">Request data portability</span>
                </li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-indigo-900/20 via-purple-900/20 to-pink-900/20 backdrop-blur-lg rounded-xl p-6 border border-indigo-500/20 shadow-xl">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 flex items-center justify-center shadow-lg">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white">
                  Contact Us
                </h3>
              </div>
              <div className="space-y-3">
                <p className="text-gray-300 text-sm leading-relaxed">
                  To exercise your privacy rights or ask questions about this policy:
                </p>
                <div className="space-y-2">
                  <p className="text-gray-300 text-sm">
                    <span className="text-indigo-400 font-semibold">Email:</span> craftingwithpixen@gmail.com
                  </p>
                  <p className="text-gray-300 text-sm">
                    <span className="text-indigo-400 font-semibold">Response Time:</span> Within 48 hours
                  </p>
                  <p className="text-gray-300 text-sm">
                    <span className="text-indigo-400 font-semibold">Verification:</span> We may need to verify your identity
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Third-Party Services Notice */}
        <div className="mb-10">
          <div className="bg-gradient-to-br from-amber-900/20 via-orange-900/20 to-gray-900/20 backdrop-blur-lg rounded-xl p-6 border border-amber-500/20 shadow-xl">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-amber-300 mb-2">
                  Third-Party Services
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed mb-3">
                  We use Razorpay and Google Drive to provide our services. These platforms have their own privacy policies:
                </p>
                <div className="space-y-1.5">
                  <a 
                    href="https://razorpay.com/privacy/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block text-indigo-400 hover:text-indigo-300 text-sm transition-colors"
                  >
                    → Razorpay Privacy Policy
                  </a>
                  <a 
                    href="https://policies.google.com/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block text-indigo-400 hover:text-indigo-300 text-sm transition-colors"
                  >
                    → Google Privacy Policy
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Updates and Changes */}
        <div className="mb-10">
          <div className="bg-gradient-to-br from-gray-900/80 via-gray-900/60 to-gray-900/80 backdrop-blur-lg rounded-xl p-6 border border-gray-800/50 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 flex items-center justify-center shadow-lg">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white">
                Changes to This Policy
              </h3>
            </div>
            <div className="space-y-3 text-gray-300 text-sm leading-relaxed">
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date.
              </p>
              <p>
                We encourage you to review this policy periodically to stay informed about how we protect your information.
              </p>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div className="text-center pt-6 border-t border-gray-800">
          <p className="text-gray-500 text-xs">
            By using ReferMe, you acknowledge that you have read and understood this Privacy Policy
          </p>
        </div>

      </div>
    </div>
    </>
  );
};

export default PrivacyPolicy; 